import React, { useMemo, useState } from 'react';
import { Check, Loader2, Monitor, Smartphone, X } from 'lucide-react';
import {
  preencherTemplate,
  renderizarSite,
  TEMPLATES,
  valoresDoCliente,
  type TemplateSite,
} from '@nyroh/contracts';
import { toast } from 'sonner';

import { ClientEntity } from '../types';
import { CDN_ASSETS } from '../lib/api';
import { db } from '../services/db';

/**
 * ============================================================================
 * A GALERIA DE MODELOS — ver antes de escolher
 * ============================================================================
 *
 * O QUE FALTAVA
 *
 * Os cinco modelos existiam no contrato e o painel não mostrava nenhum. Trocar
 * o modelo de um site era escolher um nome numa lista suspensa e publicar para
 * descobrir no que dava. Escolher às cegas e desfazer publicando de novo é o
 * fluxo que esta tela elimina.
 *
 * A PRÉVIA É O SITE, NÃO UMA IMAGEM DELE
 *
 * Nada de captura de tela guardada: cada cartão renderiza o modelo AGORA, com
 * os dados DESTE cliente, pelas mesmas `preencherTemplate` + `renderizarSite`
 * que o worker usa para publicar. O que aparece no cartão é, byte a byte, o que
 * o "Aplicar" vai publicar.
 *
 * Isso também é o que faz a prévia ser honesta quando o cadastro é magro: um
 * cliente sem resumo e sem fotos aparece sem a seção "Sobre" e sem galeria,
 * porque `preencherTemplate` descarta seção sem conteúdo. Uma captura de tela
 * genérica esconderia exatamente isso.
 *
 * É TUDO NO NAVEGADOR ATÉ O "APLICAR"
 *
 * Renderizar os cinco não custa uma requisição: as funções são puras sobre um
 * objeto pequeno. Só o "Aplicar" fala com a API.
 */

interface Props {
  client: ClientEntity;
  aoFechar: () => void;
  /** Chamado depois de publicar, para a tela de trás recarregar. */
  aoAplicar?: () => void;
}

/**
 * Traduz o cliente do painel para o formato que `valoresDoCliente` entende.
 *
 * O painel guarda o cadastro em `client.data` com nomes em inglês, herdados do
 * JSON da captação; o contrato fala em português. A tradução mora aqui, e não
 * dentro do contrato, porque é o painel que tem o formato antigo.
 */
function valoresDoClientDoPainel(client: ClientEntity) {
  const d = client.data;
  return valoresDoCliente({
    nome: d.name || client.slug,
    categoria: d.categories?.[0] ?? null,
    telefone: d.phoneNumber ?? null,
    endereco: d.address ?? null,
    dadosNegocio: {
      resumo: d.summary ?? null,
      categorias: d.categories ?? [],
      palavrasChaveSeo: d.seoKeywords ?? [],
      horarios: Array.isArray(d.openingHours) ? d.openingHours : null,
    },
  });
}

export const GaleriaDeTemplates: React.FC<Props> = ({ client, aoFechar, aoAplicar }) => {
  const [escolhido, setEscolhido] = useState<TemplateSite>(TEMPLATES[0]!);
  const [dispositivo, setDispositivo] = useState<'desktop' | 'celular'>('desktop');
  const [aplicando, setAplicando] = useState(false);

  const valores = useMemo(() => valoresDoClientDoPainel(client), [client]);

  /**
   * Os cinco HTMLs, calculados uma vez.
   *
   * Sem o memo isto refaria cinco renderizações a cada clique de seleção — e a
   * seleção é justamente o que mais muda nesta tela.
   */
  const htmls = useMemo(() => {
    const mapa = new Map<string, string>();
    for (const t of TEMPLATES) {
      try {
        mapa.set(t.id, renderizarSite(preencherTemplate(t, valores), { baseCdn: CDN_ASSETS }));
      } catch (e) {
        console.error(`[galeria] falha ao renderizar "${t.id}":`, e);
        mapa.set(
          t.id,
          '<p style="font:14px system-ui;padding:24px">Não consegui montar a prévia deste modelo.</p>',
        );
      }
    }
    return mapa;
  }, [valores]);

  const aplicar = async () => {
    const confirmar = window.confirm(
      `Publicar "${client.data.name || client.slug}" com o modelo ${escolhido.nome}?\n\n` +
        'O site que está no ar agora vira uma versão anterior — ele continua no ' +
        'histórico e você pode voltar a ele por "Colocar no ar".',
    );
    if (!confirmar) return;

    setAplicando(true);
    try {
      await db.aplicarTemplate(client.slug, escolhido.id, valores);
      toast.success(`Modelo ${escolhido.nome} publicado.`);
      aoAplicar?.();
      aoFechar();
    } catch (e) {
      console.error('[galeria] falha ao aplicar:', e);
      toast.error(e instanceof Error ? e.message : 'Não consegui publicar o modelo.');
    } finally {
      setAplicando(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
      <div className="flex h-[90vh] w-full max-w-6xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
        <header className="flex shrink-0 items-center gap-4 border-b border-gray-200 px-6 py-4">
          <div className="mr-auto">
            <h2 className="text-sm font-semibold text-gray-900">Escolher um modelo</h2>
            <p className="mt-0.5 text-xs text-gray-500">
              A prévia usa os dados de <b>{client.data.name || client.slug}</b> — é exatamente o
              que vai ao ar.
            </p>
          </div>

          <div className="flex items-center rounded-lg border border-gray-200 p-0.5">
            <button
              onClick={() => setDispositivo('desktop')}
              aria-label="Ver como no computador"
              className={`rounded-md p-1.5 transition-colors ${
                dispositivo === 'desktop' ? 'bg-gray-900 text-white' : 'text-gray-400 hover:text-gray-900'
              }`}
            >
              <Monitor size={15} />
            </button>
            <button
              onClick={() => setDispositivo('celular')}
              aria-label="Ver como no celular"
              className={`rounded-md p-1.5 transition-colors ${
                dispositivo === 'celular' ? 'bg-gray-900 text-white' : 'text-gray-400 hover:text-gray-900'
              }`}
            >
              <Smartphone size={15} />
            </button>
          </div>

          <button
            onClick={() => void aplicar()}
            disabled={aplicando}
            className="flex items-center gap-2 rounded-lg bg-gray-900 px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-black disabled:opacity-50"
          >
            {aplicando ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
            Aplicar {escolhido.nome}
          </button>
          <button
            onClick={aoFechar}
            aria-label="Fechar"
            className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-900"
          >
            <X size={18} />
          </button>
        </header>

        <div className="flex min-h-0 flex-1">
          {/* ------------------------------------------------ os cinco cartões --- */}
          <div className="w-[260px] shrink-0 overflow-y-auto border-r border-gray-200 bg-gray-50 p-3">
            <ul className="space-y-3">
              {TEMPLATES.map((t) => {
                const ativo = t.id === escolhido.id;
                return (
                  <li key={t.id}>
                    <button
                      onClick={() => setEscolhido(t)}
                      className={`w-full overflow-hidden rounded-lg border-2 bg-white text-left transition-all ${
                        ativo
                          ? 'border-gray-900 shadow-md'
                          : 'border-transparent hover:border-gray-300'
                      }`}
                    >
                      {/*
                        A miniatura é o site inteiro, encolhido — não um recorte.
                        `pointer-events-none` porque um clique aqui é "escolher
                        este modelo", não "navegar dentro da prévia".
                      */}
                      <div className="relative h-[130px] overflow-hidden bg-white">
                        <iframe
                          title={`Prévia de ${t.nome}`}
                          srcDoc={htmls.get(t.id)}
                          sandbox=""
                          scrolling="no"
                          className="pointer-events-none absolute left-0 top-0 origin-top-left"
                          style={{ width: 1200, height: 900, transform: 'scale(0.216)' }}
                        />
                      </div>
                      <div className="border-t border-gray-100 px-3 py-2">
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs font-semibold text-gray-900">{t.nome}</span>
                          {ativo && <Check size={12} className="text-gray-900" />}
                        </div>
                        <p className="mt-0.5 line-clamp-2 text-[10px] leading-snug text-gray-500">
                          {t.descricao}
                        </p>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* -------------------------------------------------- prévia grande --- */}
          <div className="flex min-w-0 flex-1 items-start justify-center overflow-auto bg-gray-100 p-6">
            <iframe
              key={`${escolhido.id}-${dispositivo}`}
              title={`Prévia grande de ${escolhido.nome}`}
              srcDoc={htmls.get(escolhido.id)}
              /*
                `sandbox=""` — nem scripts, nem mesma origem.

                O HTML vem do nosso próprio renderizador, mas os TEXTOS dentro
                dele vêm do cadastro do cliente, que veio de raspagem do Google
                Maps. Uma prévia não precisa de acesso ao painel que a hospeda, e
                a origem opaca é de graça.
              */
              sandbox=""
              className="h-full rounded-lg border border-gray-300 bg-white shadow-sm"
              style={{ width: dispositivo === 'desktop' ? '100%' : 390, minHeight: '100%' }}
            />
          </div>
        </div>

        <footer className="shrink-0 border-t border-gray-200 bg-gray-50 px-6 py-3">
          <p className="text-[11px] leading-relaxed text-gray-500">
            Aplicar publica uma <b>versão nova</b>: a que está no ar hoje continua no histórico e
            volta com um clique em <b>Colocar no ar</b>. Campo vazio no cadastro não vira espaço
            em branco — a seção sai fora, e é por isso que alguns modelos aparecem mais curtos
            para este cliente.
          </p>
        </footer>
      </div>
    </div>
  );
};
